<template>
  <div style="width: 100%;" v-loading.body="loading" class="gl-reporter">
    <b-navbar v-show="isWorksheet">
      <b-navbar-nav small>
        <b-nav-item @click="tab = 'option'" :active="tab === 'option'">
          <b-icon icon="paperclip" class="bicon"></b-icon>General Ledger Worksheet Options
        </b-nav-item>
        <b-nav-item @click="tab = 'report'" :active="tab === 'report'" :disabled="isLoading">
          <b-icon icon="table" class="bicon tbl"></b-icon>Worksheet
        </b-nav-item>
        <b-nav-item @click="tab = 'viewer'" :active="tab === 'viewer'" :disabled="!isPrinted">
          <b-icon icon="pie-chart" class="bicon"></b-icon>Viewer
        </b-nav-item>
      </b-navbar-nav>

      <b-navbar-nav v-show="tab === 'report'" class="ml-auto mr-2" small>
         <b-nav-item @click="toggleExpand" link-classes="nav-blue" v-if="displayOptions.groupingLevel === 1">
            <b-icon icon="arrows-expand" class="bicon white"></b-icon>Expand All
          </b-nav-item>
          <b-nav-item @click="toggleCollapse" link-classes="nav-red" v-else>
            <b-icon icon="arrows-collapse" class="bicon white"></b-icon>Collapse All
          </b-nav-item>
          <b-nav-item @click="refreshWorksheet" link-classes="bold" :disabled="isLoading || isSaving || isPrinting">
            <b-icon icon="arrow-repeat" class="bicon"></b-icon>Reset / Refresh
          </b-nav-item>
          <b-nav-item-dropdown class="mb-2" toggle-class="active" offset="0" :disabled="isLoading" left>
              <template slot="button-content"><b-icon icon="bullseye" class="bicon"></b-icon>Options</template>
              <b-dropdown-form>
                <b-form-checkbox v-model="displayOptions.hideDecimals">Hide Decimals</b-form-checkbox>
              </b-dropdown-form>
              <b-dropdown-form>
                  Show/Hide Columns
                  <b-form-checkbox v-for="column in displayOptions.columns" v-model="column.display" @change="onChangeVisibleColumns(column.name)" :key="column.name">
                    {{ column.displayName }}
                  </b-form-checkbox>
                  <b-dropdown-divider></b-dropdown-divider>
                  <b-dropdown-item @click="onExport" :disabled="isLoading || isSaving || isPrinting">
                    Export to Excel
                  </b-dropdown-item>
              </b-dropdown-form>
          </b-nav-item-dropdown>
          <b-nav-item @click="saveChanges" link-classes="nav-green" :disabled="isSaving || hasUpdateErrors">
            <b-icon icon="calendar-fill" class="bicon white save"></b-icon>
            <span v-if="isSaving">
              <b-spinner class="mr-2" small />Saving
            </span>
            <span v-else>
              Save
            </span>
          </b-nav-item>
          <b-nav-item-dropdown class="nav-blue mb-1 mr-1" toggle-class="active print" offset="0" :disabled="isLoading" right>
            <template slot="button-content">
              <b-icon v-if="!isPrinting" icon="printer-fill" class="bicon white"></b-icon>
              <span v-if="isPrinting">
                <b-spinner class="mr-2" small /> Printing
              </span>
              <span v-else>
                Print
              </span>
            </template>
            <b-dropdown-form>
              <b-dropdown-item @click="printPdf('detail')">
                Detail Report
              </b-dropdown-item>
              <b-dropdown-item @click="printPdf('summary')">
                Summary Report
              </b-dropdown-item>
            </b-dropdown-form>
          </b-nav-item-dropdown>
        </b-navbar-nav>
    </b-navbar>

    <b-container v-show="tab === 'option'" fluid id="options" ref="options">
      <el-row type="flex" align="middle" justify="end" class="headerRow">
        <el-col>
          <el-row type="flex" align="middle" justify="start" style="font-size: 1.5em;">
            GENERAL LEDGER REPORTING
          </el-row>
        </el-col>
        <el-col class="small">
            <a @click="showJson = ! showJson">.&nbsp;</a>
        </el-col>
        <el-col>
          <el-row type="flex" align="middle" justify="end">
            <el-button type="primary" @click="buildWorkSheet" v-if="!isReady">
              Build Worksheet
            </el-button>
            <el-button type="primary" v-else>
              <b-spinner class="mr-2" small /> Please Wait
            </el-button>
            <el-button plain @click="generateReport" :disabled="disableReportRun">
              Run report
            </el-button>
          </el-row>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3" class="right">
          Saved Report Options
        </el-col>
        <el-col :span="4">
          <el-select v-model="selectedOption" :filterable="true" @change="handleSelection" data-fieldtype="select" :multiple="false" clearable placeholder="Search">
              <el-option
                v-for="opt in savedReportOptions"
                :key="opt.id"
                :label="opt.report_name"
                :value="opt.id">
              </el-option>
            </el-select>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3" class="right">
          Report Options
        </el-col>
        <el-col :span="20">
          <el-button @click="openSaveOption">Save Report Options</el-button>
          <el-button @click="loadOptions">Load Report Options</el-button>
          <el-button @click="deleteOptions">Delete Report Options</el-button>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="end" class="headerRow">
        <el-col>
          <el-row type="flex" align="middle" justify="start" style="font-size: 1.2em;">
            Report Options
          </el-row>
        </el-col>
      </el-row>

      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3" class="right">
          Summary/Detail
        </el-col>
        <el-col :span="4">
          <el-radio-group v-model="reportOptions.type" @change="updateReportType">
            <el-radio-button label="detail">Detail</el-radio-button>
            <el-radio-button label="summary">Summary</el-radio-button>
          </el-radio-group>
        </el-col>
      </el-row>

      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3" class="right">
          Periods/Dates
        </el-col>
        <el-col :span="21">
          <el-radio-group v-model="reportOptions.dates" @change="updateReportDates">
            <el-radio-button label="datesAll">All</el-radio-button>
            <el-radio-button label="datesPeriod">Period</el-radio-button>
            <el-radio-button label="datesPosted">Posting Date</el-radio-button>
            <el-radio-button label="datesEffective">Effective Date</el-radio-button>
          </el-radio-group>
        </el-col>
      </el-row>

      <div v-if="reportOptions.dates==='datesPeriod'" class="m-top-20">
        <el-row type="flex" align="middle" justify="start">
          <el-col :span="3"></el-col>
          <el-col :span="4">Starting Period</el-col>
          <el-col :span="3"></el-col>
          <el-col :span="4">Ending Period</el-col>
        </el-row>
        <el-row type="flex" align="middle" justify="start">
          <el-col :span="3"></el-col>
          <el-col :span="4">
            <el-select v-model="selectedCodes.period_start" :multiple="false" placeholder="First Period">
              <el-option
                v-for="opt in allCodes.periods"
                :key="opt.id"
                :label="opt.id + ' - ' + opt.start"
                :value="opt.id">
              </el-option>
            </el-select>
          </el-col>
          <el-col :span="3"></el-col>
          <el-col :span="4">
            <el-select v-model="selectedCodes.period_end" :multiple="false" placeholder="Last Period">
              <el-option
                v-for="opt in allCodes.periods"
                :key="opt.id"
                :label="opt.id + ' - ' + opt.start"
                :value="opt.id">
              </el-option>
            </el-select>
          </el-col>
        </el-row>
      </div>

      <div v-if="reportOptions.dates==='datesPosted'" class="m-top-20">
        <el-row type="flex" align="middle" justify="start">
          <el-col :span="3" />
          <el-col :span="4">Posting Date Start</el-col>
          <el-col :span="3" />
          <el-col :span="4">Posting Date End</el-col>
        </el-row>
        <el-row type="flex" align="middle" justify="start">
          <el-col :span="3" />
          <el-col :span="4">
            <DateField v-model="selectedCodes.time_posted_start" :key="reRender" />
          </el-col>
          <el-col :span="3" />
          <el-col :span="4">
            <DateField v-model="selectedCodes.time_posted_end" :key="reRender" />
          </el-col>
        </el-row>
      </div>

      <div v-if="reportOptions.dates==='datesEffective'" class="m-top-20">
        <el-row type="flex" align="middle" justify="start">
          <el-col :span="3" />
          <el-col :span="4">Effective Date Start <sup>**</sup></el-col>
          <el-col :span="3" />
          <el-col :span="4">Effective Date End <sup>**</sup></el-col>
        </el-row>
        <el-row type="flex" align="middle" justify="start">
          <el-col :span="3" />
          <el-col :span="4">
            <DateField v-model="selectedCodes.effective_date_start" :key="reRender"/>
          </el-col>
          <el-col :span="3" />
          <el-col :span="4">
            <DateField v-model="selectedCodes.effective_date_end" :key="reRender"/>
          </el-col>
        </el-row>
        <el-row v-if="reportOptions.dates==='datesEffective'" type="flex" align="middle" justify="start">
          <el-col :span="3" />
          <el-col :span="21"><p><sup>**</sup> Posting Date is used if Effective Date is not Set</p></el-col>
        </el-row>
      </div>

      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3" class="right">
          Company
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="selectedCodes.company_id"
            :multiple="false" placeholder="All">
            <el-option
              v-for="opt in allCodes.companies"
              :key="opt.id"
              :label="opt.code + ' - ' + opt.name"
              :value="opt.id">
            </el-option>
          </el-select>
        </el-col>
        <el-col :span="3" class="right">
          Production
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="selectedCodes.production_id"
            :multiple="true" placeholder="All">
            <el-option
              v-for="opt in allCodes.productions"
              :key="opt.id"
              :label="opt.code + ' - ' + opt.name"
              :value="opt.id">
            </el-option>
          </el-select>
        </el-col>
      </el-row>

      <el-row type="flex" align="middle" justify="start" class="selectRow">
        <el-col :span="3" class="right">
          Series
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="selectedCodes.series_id"
            :multiple="true" placeholder="All">
            <el-option
              v-for="opt in allCodes.series"
              :key="opt.id"
              :label="opt.code + ' - ' + opt.name"
              :value="opt.id">
            </el-option>
          </el-select>
        </el-col>
        <el-col :span="3" class="right">
          Location
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="selectedCodes.location_id"
            :multiple="true" placeholder="All">
            <el-option
              v-for="opt in allCodes.locations"
              :key="opt.id"
              :label="opt.code + ' - ' + opt.name"
              :value="opt.id">
            </el-option>
          </el-select>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="selectRow">
        <el-col :span="3" class="right">
          Set
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="selectedCodes.set_id"
            :multiple="true" placeholder="All">
            <el-option
              v-for="opt in allCodes.sets"
              :key="opt.id"
              :label="opt.code + ' - ' + opt.name"
              :value="opt.id">
            </el-option>
          </el-select>
        </el-col>
        <el-col :span="3" class="right">
          Free Field
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="selectedCodes.free_field_1_id"
            :multiple="true" placeholder="All">
            <el-option
              v-for="opt in allCodes.freefield1"
              :key="opt.id"
              :label="opt.code + ' - ' + opt.name"
              :value="opt.id">
            </el-option>
          </el-select>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3" class="right">
          Source Code
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="selectedCodes.source_code_id"
            :multiple="true" placeholder="All">
            <el-option
              v-for="opt in allCodes.sourcecodes"
              :key="opt.id"
              :label="opt.code + ' - ' + opt.name"
              :value="opt.id">
            </el-option>
          </el-select>
        </el-col>
        <el-col :span="3" class="right">
          Assets
        </el-col>
        <el-col :span="4">
          <el-select v-model="selectedCodes.asset" placeholder="All">
            <el-option value="true" label="Show Only Assets"></el-option>
            <el-option value="false" label="Hide Assets"></el-option>
            <el-option :value="undefined">All Transactions</el-option>
          </el-select>
        </el-col>
      </el-row>
      <el-row v-if="(allCodes.currencies||[]).length > 1" type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3" class="right">
          Currency
        </el-col>
        <el-col :span="4">
          <el-select v-model="selectedCodes.currency_id" :multiple="true" placeholder="All">
            <el-option
              v-for="opt in currencies"
              :key="opt.id"
              :label="opt.code + ' - ' + parseFloat(opt.rate_vs_usd || 0).toFixed(4)"
              :value="opt.id">
            </el-option>
          </el-select>
        </el-col>
        <el-col :span="3" class="right">
          Convert To
        </el-col>
        <el-col :span="4">
          <el-select v-model="reportOptions.report_currency_id">
            <el-option
              v-for="opt in currencies"
              :key="opt.id"
              :label="opt.code + ' - ' + parseFloat(opt.rate_vs_usd || 0).toFixed(4)"
              :value="opt.id">
            </el-option>
          </el-select>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3" class="right">
          Report Output
        </el-col>
        <el-col :span="4">
          <el-select v-model="reportOptions.format">
            <el-option
              v-for="opt in reportOutputFormats"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value">
            </el-option>
          </el-select>
        </el-col>      
        <el-col :span="3" class="right" v-if="showUnposted">
          Include
        </el-col>
        <el-col :span="4" v-if="showUnposted">
          <el-checkbox v-model="reportOptions.includeunposted"></el-checkbox> Unposted Items
        </el-col>
      </el-row>
      <el-row v-if="reportOptions.format === 'csv'" type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="3" class="right">
          Subtotal by Account
        </el-col>
        <el-col :span="4">
          <el-checkbox v-model="csvSubTotalByAccount" /> 
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="headerRow"> 
        <el-col :span="3" class="right">
          Include
        </el-col>
        <el-col :span="1">
          <el-checkbox v-model="transactionTypes.pos.include"></el-checkbox> POs
        </el-col>
        <el-col :span="1">
          <el-checkbox v-model="transactionTypes.aps.include"></el-checkbox> APs
        </el-col>
        <el-col :span="1">
          <el-checkbox v-model="transactionTypes.jes.include"></el-checkbox> JEs
        </el-col>
        <el-col :span="1">
          <el-checkbox v-model="transactionTypes.pcs.include"></el-checkbox> PCs
        </el-col>
        <el-col :span="1">
          <el-checkbox v-model="transactionTypes.prs.include"></el-checkbox> PRs
        </el-col>
        <el-col :span="1">
          <el-checkbox v-model="transactionTypes.cks.include"></el-checkbox> CKs
        </el-col>
        <el-col :span="1">
          <el-checkbox v-model="transactionTypes.wts.include"></el-checkbox> WTs
        </el-col>
        <el-col :span="1">
          <el-checkbox v-model="transactionTypes.mcs.include"></el-checkbox> MCs
        </el-col>
        <el-col :span="2">
          <el-checkbox v-model="transactionTypes.efts.include"></el-checkbox> EFTs
        </el-col>
      </el-row>
      <div class="clear"></div>
      <el-row type="flex" align="middle" justify="end" class="section">
        <el-col>
          <el-row type="flex" align="middle" justify="start" style="font-size: 1.2em;">
            Vendor Selection
          </el-row>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="24" :offset="1">
          <el-radio-group v-model="reportOptions.vendorQuickPick" @change="updateVendors" :disabled="loading || disableReportRun">
            <el-radio-button label="custom">Custom</el-radio-button>
            <el-radio-button label="all">All</el-radio-button>
          </el-radio-group>
        </el-col>
      </el-row>
      <el-row v-show="reportOptions.vendorQuickPick==='custom'" type="flex" align="top" justify="start" class="headerRow">
        <el-col :span="24" :offset="1">
          <el-transfer
            ref="vendorTransfer"
            v-model="selectedCodes.vendor_id"
            :data="allCodes.vendors"
            :titles="[ 'Unselected Vendors', 'Selected Vendors' ]"
            :props="{ key: 'id', label: 'display_name' }"
            filterable
            filter-placeholder="Search Vendors"
          >
          </el-transfer>
        </el-col>
      </el-row>
      <div class="clear"></div>
      <el-row type="flex" align="middle" justify="end" class="section">
        <el-col>
          <el-row type="flex" align="middle" justify="start" style="font-size: 1.2em;">
            Account Selection
          </el-row>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="24" :offset="1">
          <el-radio-group v-model="reportOptions.accountQuickPick" @change="updateAccounts" :disabled="loading || disableReportRun">
            <el-radio-button label="custom">Custom</el-radio-button>
            <el-radio-button label="range">Range</el-radio-button>
            <el-radio-button label="all">All</el-radio-button>
            <el-radio-button label="expenses">Expenses Only</el-radio-button>
            <el-radio-button label="balance">Balance Sheet Only</el-radio-button>
            <el-radio-button label="none">None</el-radio-button>
          </el-radio-group>
        </el-col>
      </el-row>
      <div v-if="reportOptions.accountQuickPick==='range'" class="m-top-20">
        <el-row type="flex" align="middle" justify="start">
          <el-col :span="3"></el-col>
          <el-col :span="4">From</el-col>
          <el-col :span="3"></el-col>
          <el-col :span="4">To</el-col>
        </el-row>
        <el-row type="flex" align="middle" justify="start">
          <el-col :span="3"></el-col>
          <el-col :span="4">
            <el-select v-model="selectedCodes.account_id2" clearable :multiple="false" placeholder="First Account" :filterable="true" @change="checkCodeValidity">
              <el-option
                v-for="opt in allCodes.accounts"
                :key="opt.id"
                :label="opt.code + ' - ' + opt.name"
                :value="opt.id">
              </el-option>
            </el-select>
          </el-col>
          <el-col :span="3"></el-col>
          <el-col :span="4">
            <el-select v-model="selectedCodes.account_id1" clearable :multiple="false" placeholder="Last Account" :filterable="true" @change="checkCodeValidity">
              <el-option
                v-for="opt in filteredOptions2"
                :key="opt.id"
                :label="opt.code + ' - ' + opt.name"
                :value="opt.id">
              </el-option>
            </el-select>
          </el-col>
        </el-row>
      </div>

      <el-row v-show="reportOptions.accountQuickPick==='custom'" type="flex" align="top" justify="start" class="headerRow">
        <el-col :span="24" :offset="1">
          <el-transfer
            ref="accountTransfer"
            v-model="selectedCodes.account_id"
            :data="accounts"
            :titles="[ 'Unselected Accounts', 'Selected Accounts' ]"
            :props="{ key: 'id', label: 'display_name' }"
            filterable
            filter-placeholder="Search Accounts"
          >
          </el-transfer>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="end" class="headerRow">
        <el-button plain @click="generateReport" :disabled="disableReportRun">
          Run report
        </el-button>
      </el-row>
      <el-row v-if="showJson">
        <el-col>
          <textarea id="output" style="width: 100%; height: 500px;" v-model="prettyJson"></textarea>
        </el-col>
      </el-row>
      <el-row v-if="showJson">
        <el-col :span="1">
          <el-button plain @click="generateCustomReport" :disabled="disableReportRun">
            Run Customized Report
          </el-button>
        </el-col>
      </el-row>
    </b-container>
    <b-container v-show="tab === 'report'" fluid>
      <ag-grid-vue
        name="worksheet"
        ref="worksheet"
        class="ag-theme-balham"
        :style="{ width, height }"
        :key="iteration"
        :column-defs="columnDefs"
        :row-data="rowData"
        :grid-options="gridOptions"
        :auto-group-column-def="autoGroupColumnDef"
        :isExternalFilterPresent="isExternalFilterPresent"
        :doesExternalFilterPass="doesExternalFilterPass"
      />
    </b-container>
    <b-container v-show="tab === 'viewer'" fluid>
      <basic-viewer v-if="pdfData" :pdf-data="pdfData" :width="width" :height="height" />
      <span v-else> No Data </span>
    </b-container>
    <el-dialog
    title="Report Options"
    size="tiny"
    :visible="visible"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false">
    <el-row type="flex" align="middle" element-loading-text='Saving...'>
      <el-col>
        <el-row type="flex" align="middle">
          <el-form :model="form" ref="authForm" v-if="selectedReportOptions">
            <el-form-item style="margin-bottom: 6px;">
              Do you wish to update the Report Options saved under {{ selectedReportOptions.report_name }}
            </el-form-item>
          </el-form>
          <el-form :model="form" ref="authForm" v-if="empty_error">
            <el-form-item style="margin-bottom: 6px;">
              Please select a set of Report Options to load.
            </el-form-item>
          </el-form>
          <el-form :model="form" ref="authForm" v-if="delete_error">
            <el-form-item style="margin-bottom: 6px;">
              Please select a set of Report Options to delete.
            </el-form-item>
          </el-form>
          <el-form :model="form" ref="authForm" v-if="!selectedReportOptions && !empty_error && !delete_error">
            <el-form-item style="margin-bottom: 6px;">
              <el-input placeholder="Name" v-model="savereportoptions" ref="nameField">
              </el-input>
            </el-form-item>
          </el-form>
        </el-row>
      </el-col>
    </el-row>
    <el-row v-if="error" type="flex" align="middle" justify="center" style="padding-top: 12px;">
      <el-tag type="danger">{{ error }}</el-tag>
    </el-row>
    <span slot="footer" class="dialog-footer">
      <el-button @click="visible = false" class="auth-btn" v-if="!empty_error && !delete_error">
        Cancel
      </el-button>
      <el-button @click="saveOptions" class="auth-btn" type="primary">
        OK
      </el-button>
    </span>
  </el-dialog>
  <el-dialog
    title="Delete Report Options"
    size="tiny"
    :visible="visible_delete"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false">
    <el-row type="flex" align="middle" element-loading-text='Saving...'>
      <el-col>
        <el-row type="flex" align="middle">
          <el-form :model="form" ref="authForm">
            <el-form-item style="margin-bottom: 6px;">
              Do you want to delete report options?
            </el-form-item>
          </el-form>
        </el-row>
      </el-col>
    </el-row>
    <el-row v-if="error" type="flex" align="middle" justify="center" style="padding-top: 12px;">
      <el-tag type="danger">{{ error }}</el-tag>
    </el-row>
    <span slot="footer" class="dialog-footer">
      <el-button @click="visible_delete = false" class="auth-btn">
        Cancel
      </el-button>
      <el-button @click="confirmDeleteOptions" class="auth-btn" type="primary">
        OK
      </el-button>
    </span>
  </el-dialog>
  </div>
</template>

<script>
  /* eslint-disable no-unused-vars */
  import { sortObjectByProperty } from '@/utils/helpers/ObjectSorting'
  import { capitalizeFirstLetter } from '@/utils/helpers/StringOperations'
  import { reportMixins } from '@/Mixins/Reports'
  import DateField from '@/components/InputComponents/DateField.vue'
  import { notify } from '@/utils/helpers/Notify'
  import { AgGridVue } from 'ag-grid-vue'
  import 'ag-grid-enterprise'
  import AgGridColumns from '@/Mixins/SigmaClientNew/GeneralLedgerWorksheet/Columns'
  import AgGridMethodsMixins from '@/Mixins/SigmaClientNew/GeneralLedgerWorksheet/Methods'
  import BasicViewer from '@/components/Reports/BasicViewer.vue'
  import moment from 'moment'
  import { BIcon, BIconPaperclip, BIconTable, BIconArrowsExpand, BIconArrowsCollapse, BIconArrowRepeat, BIconLayersFill, BIconBullseye, BIconCalendarFill, BIconPieChart, BIconPrinterFill } from 'bootstrap-vue'

  export default {
    name: 'GlReport',
    components: {
      AgGridVue,
      DateField,
      notify,
      BasicViewer,
      BIcon,
      BIconPaperclip,
      BIconTable,
      BIconArrowRepeat,
      BIconLayersFill,
      BIconBullseye,
      BIconCalendarFill,
      BIconPrinterFill,
      BIconArrowsExpand,
      BIconPieChart,
      BIconArrowsCollapse
    },
    mixins: [ AgGridColumns, AgGridMethodsMixins, reportMixins ],
    data () {
      const { getJasperHost } = this.$store.getters
      return {
        csvSubTotalByAccount: false,
        filteredAccounts: [],
        effectiveDateMandatory: false,
        // Grid related
        iteration: 0, // Key for Ag-Grid to refresh
        gridOptions: null,
        gridApi: null,
        columnApi: null,
        columnDefs: null,
        rowData: null,
        defaultColDef: null,
        autoGroupColumnDef: null,
        isLoading: true,
        isSaving: false,
        isPrinting: false,
        isReady: false,
        isWorksheet: false,
        isPrinted: false,
        tab: 'option',
        scope: this,
        width: '100%',
        pdfWidth: '100%',
        pdfHeight: '100%',
        workSheetData: [],
        worksheetUpdated: false,
        jeLinesToBeUpdated: [],
        poLinesToBeUpdated: [],
        pcLinesToBeUpdated: [],
        dcColumns: [],
        dcAllowedColumns: [],
        dcAllowedColumnMap: {
          account: 'Account',
          series: 'Series',
          location: 'Location',
          set: 'Set',
          description: 'Description',
          free_field_1: 'FF1',
          free_field_2: 'FF2',
          free_field_3: 'FF3',
          free_field_4: 'FF4',
          tax_code: 'Tax',
          asset: 'Asset'
        },
        hasUpdateErrors: false,
        pdfData: undefined,
        reportData: '',
        originalLines: '',
        displayOptions: {
          groupingLevel: 2,
          reportTemplateId: 1,
          hideDecimals: false,
          columns: [
            { name: 'line_desc', displayName: 'GL Description', display: true },
            { name: 'vendor_name', displayName: 'Vendor', display: true },
            { name: 'series', displayName: 'Ser', display: true },
            { name: 'location', displayName: 'Loc', display: true },
            { name: 'set', displayName: 'Set', display: true },
            { name: 'free_field_1', displayName: 'FF1', display: true },
            { name: 'free_field_2', displayName: 'FF2', display: true },
            { name: 'free_field_3', displayName: 'FF3', display: true },
            { name: 'free_field_4', displayName: 'FF4', display: true },
            { name: 'ins', displayName: 'Ins', display: true },
            { name: 'tax_code', displayName: '9', display: true },
            { name: 'asset', displayName: 'Asset', display: true },
            { name: 'doc_number', displayName: 'Doc #', display: true },
            { name: 'transaction_currency', displayName: 'Cur', display: true }
          ]
        },
        reportOptions: {
          transactionStatuses: [ 'posted', 'closed', 'reversed' ],
          accountQuickPick: 'all',
          vendorQuickPick: 'all',
          type: 'detail',
          dates: 'datesAll',
          sort: 'account',
          status: 'all',
          format: 'pdf',
          currencyFactor: undefined,
          report_currency_id: 1,
          includeunposted: false
        },
        selectedCodes: undefined,
        report_group: 'General_Ledger',
        savereportoptions: '',
        isBusy: false,
        visible: false,
        empty_error: false,
        delete_error: false,
        selectedReportOptions: null,
        savedReportOptions: [],
        selectedOption: null,
        visible_delete: false,
        form: {save_options: { label: '', value: '' }},
        newAddedReport: undefined,
        reRender: 0,
        allCodes: {},    // Show codes, e.g. accounts, series, locations, sets, etc.
        jasperSettings: {
          reportServer: `${getJasperHost}rest_v2/reports/Revolution/Phoenix/`,
          reportName: 'general_ledger_detail'
        },
        vendors: undefined,
        transactionTypes: undefined,
        jeIncludedForPr: false,
        unserializedData: {      // Raw data from API
          aps: {
            headers: {},
            lines: {}
          },
          jes: {
            headers: {},
            lines: {}
          },
          pcs: {
            headers: {},
            lines: {}
          },
          prs: {
            headers: {},
            lines: {}
          },
          cks: {
            headers: {},
            lines: {}
          },
          pos: {
            headers: {},
            lines: {}
          },
          efts: {
            headers: {},
            lines: {}
          },
          wts: {
            headers: {},
            lines: {}
          },
          mcs: {
            headers: {},
            lines: {}
          }
        },
        reportOutputFormats: [
          { value: 'pdf', label: 'PDF' },
          { value: 'csv', label: 'CSV' }
        ],
        disableReportRun: false, // Disable run report button if false
        reportJson: undefined,
        prettyJson: undefined,
        showJson: false,
        reportNotif: undefined,
        collapseExpenses: false,
        loading: false
      }
    },

    computed: {
      filteredOptions2 () {
        return this.allCodes.accounts.filter((option) => option.id !== this.selectedCodes.account_id)
      },
      username () { return this.$store.getters.getUser.username },
      includedTypes () {
        if (this.selectedCodes.source_code_id.length) {
          this.transactionTypes.jes.include = true
          if (this.selectedCodes.source_code_id.includes(5)) {
            this.transactionTypes.prs.include = true
          }
        }
        return Object.values(this.transactionTypes).filter(types => types.include === true).map(type => type.name)
      },
      accounts () {
        let accountsNew = []
        for (let i in this.allCodes.accounts) {
          if (this.allCodes.accounts[i].type.toLowerCase() === 'header') continue
          accountsNew[i] = this.allCodes.accounts[i]
          accountsNew[i]['display_name'] = this.allCodes.accounts[i]['code'] + ' - ' + this.allCodes.accounts[i]['name']
        }
        return accountsNew
      },
      currencies () {
        if (typeof this.allCodes.currencies !== 'undefined' && this.allCodes.currencies.length > 0) {
          return sortObjectByProperty(this.allCodes.currencies, 'id')
        } else {
          return []
        }
      },
      seriesCodes () {
        const selected = (this.allCodes.series || []).filter((ser) => this.selectedCodes.series_id.includes(ser.id))
        if (selected.length === 0) return 'All'
        else return selected.map((ser) => ser.code).join(', ')
      },
      locationCodes () {
        const selected = (this.allCodes.locations || []).filter((loc) => this.selectedCodes.location_id.includes(loc.id))
        if (selected.length === 0) return 'All'
        else return selected.map((loc) => loc.code).join(', ')
      },
      setCodes () {
        const selected = (this.allCodes.sets || []).filter((set) => this.selectedCodes.set_id.includes(set.id))
        if (selected.length === 0) return 'All'
        else return selected.map((set) => set.code).join(', ')
      },
      freeFieldCodes () {
        const selected = (this.allCodes.freefield1 || []).filter((ff) => this.selectedCodes.free_field_1_id.includes(ff.id) || this.selectedCodes.free_field_2_id.includes(ff.id) || this.selectedCodes.free_field_3_id.includes(ff.id) || this.selectedCodes.free_field_4_id.includes(ff.id))
        if (selected.length === 0) return 'All'
        else return selected.map((ff) => ff.code).join(', ')
      },
      sourceCodes () {
        const selected = (this.allCodes.sourcecodes || []).filter((sc) => this.selectedCodes.source_code_id.includes(sc.id))
        if (selected.length === 0) return 'All'
        else return selected.map((sc) => sc.code).join(', ')
      },
      seriesNames () {
        const selected = (this.allCodes.series || []).filter((ser) => this.selectedCodes.series_id.includes(ser.id))
        if (selected.length === 0) return ''
        else return selected.map((ser) => ser.name).join(', ')
      },
      locationNames () {
        const selected = (this.allCodes.locations || []).filter((loc) => this.selectedCodes.location_id.includes(loc.id))
        if (selected.length === 0) return ''
        else return selected.map((loc) => loc.name).join(', ')
      },
      setNames () {
        const selected = (this.allCodes.sets || []).filter((set) => this.selectedCodes.set_id.includes(set.id))
        if (selected.length === 0) return ''
        else return selected.map((set) => set.name).join(', ')
      },
      dateStart () {
        switch (this.reportOptions.dates) {
          case 'datesPeriod':
            return (this.allCodes.periods.find(({id}) => id === this.selectedCodes.period_start) || {}).start || '0000/00/00'

          case 'datesEffective':
            return this.selectedCodes.effective_date_start || '0000/00/00'

          case 'datesPosted':
            return this.selectedCodes.time_posted_start || '0000/00/00'

          default:
            return 'ALL'
        }
      },
      dateEnd () {
        switch (this.reportOptions.dates) {
          case 'datesPeriod':
            return (this.allCodes.periods.find(({id}) => id === this.selectedCodes.period_end) || {}).end || '9999/99/99'

          case 'datesEffective':
            return this.selectedCodes.effective_date_end || '9999/99/99'

          case 'datesPosted':
            return this.selectedCodes.time_posted_end || '9999/99/99'

          default:
            return 'ALL'
        }
      },
      showUnposted () {
        return this.$store.getters.getUiSettings['GL_REPORT_DEFAULT_SETTINGS'] && JSON.parse(this.$store.getters.getUiSettings['GL_REPORT_DEFAULT_SETTINGS']).showUnposted ? JSON.parse(this.$store.getters.getUiSettings['GL_REPORT_DEFAULT_SETTINGS']).showUnposted : false
      },
      hideDecimals () {
        return this.$data.displayOptions.hideDecimals
      },
      height () {
        // Reducing by 3 % to handle sigma-frontend navbar
        return window.innerHeight * 0.88 + 'px'
      }
    },
    methods: {
      checkCodeValidity () {
        const fromValue = this.selectedCodes.account_id2 !== '' ? this.allCodes.accounts.filter((account) => account.id === this.selectedCodes.account_id2) : this.allCodes.accounts.filter((element, index) => index === 0)
        const toValue = this.selectedCodes.account_id1 !== '' ? this.allCodes.accounts.filter((account) => account.id === this.selectedCodes.account_id1) : this.allCodes.accounts.filter((element, index) => index === this.allCodes.accounts.length - 1)
        if (fromValue && toValue) {
          if (fromValue[0].code > toValue[0].code) {
            this.disableReportRun = true
            notify(this, 'error', 'The To Account cannot be before the From Account. Please update your selection.')
          } else {
            if (this.selectedCodes.account_id2 === [] || this.selectedCodes.account_id1 === []) {
              this.selectedCodes.account_id = this.allCodes.accounts.map((account) => account.id)
            } else {
              this.disableReportRun = false
              this.filteredAccounts = this.allCodes.accounts.filter((account) => account.code >= fromValue[0].code && account.code <= toValue[0].code)
              this.selectedCodes.account_id = this.filteredAccounts.map((account) => account.id)
            }
          }
        }
      },
      resetComponent () {
        this.clearStore()
        this.resetFilters()
      },
      clearStore () {
        // TODO
      },
      updateReportType (type) {
        this.jasperSettings.reportName = 'general_ledger_' + type
      },
      updateReportDates (type) {
        this.selectedCodes.period_start = this.selectedCodes.period_end = this.selectedCodes.time_posted_start = this.selectedCodes.time_posted_end = this.selectedCodes.effective_date_start = this.selectedCodes.effective_date_end = undefined
      },
      updateAccounts (type) {
        if (!type) return false
        switch (type) {
          case 'none':
            this.disableReportRun = false
            this.selectedCodes.account_id = []
            break
          case 'all':
            this.disableReportRun = false
            this.selectedCodes.account_id = this.allCodes.accounts.map(acct => acct.id)
            break
          case 'expenses':
            this.disableReportRun = false
            this.selectedCodes.account_id = this.allCodes.accounts.filter(({type}) => type.toLowerCase().match('expense')).map(acct => acct.id)
            break
          case 'balance':
            this.disableReportRun = false
            this.selectedCodes.account_id = this.allCodes.accounts.filter(({type}) => {
              if (type.toLowerCase().match('asset') || type.toLowerCase().match('liability') || type.toLowerCase().match('income')) return true
            }).map(acct => acct.id)
            break
          case 'custom':
            this.disableReportRun = false
            break
          case 'range':
            this.disableReportRun = false
            break
          default:
            return undefined
        }
        // The transfer table is one tick behind.  Force update now
        this.$forceUpdate()
        return true
      },
      updateVendors (type) {
        if (!type) return false
        switch (type) {
          case 'none':
            this.disableReportRun = true
            this.selectedCodes.vendor_id = []
            break
          case 'all':
            this.disableReportRun = false
            this.selectedCodes.vendor_id = []
            break
          case 'custom':
            this.disableReportRun = false
            break
          default:
            return undefined
        }
        // The transfer table is one tick behind.  Force update now
        this.$forceUpdate()
        return true
      },
      addExpenseAccounts () {
        this.selectedCodes.account_id = this.allCodes.accounts.filter(({type}) => type.match('EXPENSE')).map(acct => acct.id)
      },
      includeAps ({target}) {
        this.transactionTypes.aps.include = target.checked
      },
      includeJes ({target}) {
        this.transactionTypes.jes.include = target.checked
      },
      includePcs ({target}) {
        this.transactionTypes.pcs.include = target.checked
      },
      includePrs ({target}) {
        this.transactionTypes.prs.include = target.checked
      },
      includeCks ({target}) {
        this.transactionTypes.cks.include = target.checked
      },
      includePos ({target}) {
        this.transactionTypes.pos.include = target.checked
      },
      includeEfts ({target}) {
        this.transactionTypes.efts.include = target.checked
      },
      includeWts ({target}) {
        this.transactionTypes.wts.include = target.checked
      },
      includeMcs ({target}) {
        this.transactionTypes.mcs.include = target.checked
      },
      selectReportFormat ({target}) {
        if (target.checked) {
          this.reportOptions.format = 'csv'
        } else {
          this.reportOptions.format = 'pdf'
        }
      },
      buildCsvData (sortedAccounts) {
        if (!sortedAccounts || !Array.isArray(sortedAccounts)) return false

        let detailedColumns = { 'Company': 'company_name', 'Production': 'production_name', 'Transaction Type': 'trans_type', 'Transaction ID': 'trans_number', 'Transaction Description': 'transaction_description', 'Transaction Line Description': 'line_desc', 'Account': 'account', 'Account Description': 'account_name', 'Vendor Code': 'transaction_vendor', 'Vendor Name': 'vendor_name', 'Series': 'series', 'Location': 'location', 'Set': 'set', 'FF1': 'free_field_1', 'FF2': 'free_field_2', 'FF3': 'free_field_3', 'FF4': 'free_field_4', 'Tax Code': 'tax_code', 'Asset': 'asset', 'Document Number': 'doc_number', 'Document Date': 'doc_date', 'Posting Date': 'transaction_time_posted', 'Effective Date': 'transaction_effective_date', 'Period': 'transaction_period_id', 'PO Number': 'po_number', 'Check Number': 'payment_number', 'Check Amount': 'transaction_payment_amount', 'Bank Recon': 'recon', 'Cur': 'transaction_currency', 'Amount': 'amount' }
        let summaryColumns = { 'Account': 'account_number', 'Account Description': 'account_desc', 'Amount': 'account_total' }

        // TODO: Sipra Output
        let columnMap = (this.reportOptions.type === 'summary') ? summaryColumns : detailedColumns

        return new Promise((resolve, reject) => {
          let ledgerData = []

          sortedAccounts.map(accountCode => {
            if (this.reportOptions.type === 'summary') {
              accountCode.transaction = undefined
              ledgerData.push(accountCode)
            } else {
              accountCode.transaction.map(row => ledgerData.push(row))
            }
          })

          let finalCsv
          this.disableReportRun = false
          ledgerData.map(data => {
            data.amount = data.amount ? parseFloat(data.amount).toFixed(2) : data.amount
            data.transaction_payment_amount = data.transaction_payment_amount ? parseFloat(data.transaction_payment_amount).toFixed(2) : data.transaction_payment_amount
          })
          if (this.csvSubTotalByAccount) { finalCsv = this.createGroupByAccountCsv(ledgerData, columnMap) } else { finalCsv = this.createCsv(ledgerData, columnMap) }

          if (finalCsv) resolve(finalCsv)
          else reject('Could not build CSV.')
        })
      },

      buildGlReport (sortedAccounts) {
        return new Promise((resolve, reject) => {
          let json = {
            report: {
              report_title: `General Ledger ${capitalizeFirstLetter(this.reportOptions.type)}`,
              report_subtitle: '',
              report_name: 'general_ledger_detail',
              printed_by: this.username,
              date_run: this.today(),
              company: (this.selectedCodes.company_id) ? (this.allCodes.companies.find(({id}) => id === this.selectedCodes.company_id).name) : this.allCodes.companies[0].name,
              project:
                this.selectedCodes.production_id && this.selectedCodes.production_id.length
                ? this.allCodes.productions.filter((prod) => this.selectedCodes.production_id.includes(prod.id)).map((prod) => prod.name).join(', ')
                : this.allCodes.productions[0].name,
              currency: (this.reportOptions.report_currency_id) ? (this.allCodes.currencies.find(({id}) => id === this.reportOptions.report_currency_id).code) : this.currencies[0].code,
              period: this.dateStart + ' - ' + this.dateEnd,
              date_start: this.dateStart,
              date_end: this.dateEnd,
              grand_total: '',
              transactions: 'All',
              // Episodes is legacy
              episodes: (this.selectedCodes.series_id && this.selectedCodes.series_id.length > 0) ? (this.allCodes.series.find(({id}) => this.selectedCodes.series_id.includes(id)).code) : 'All',
              series: this.seriesCodes,
              seriesNames: this.seriesNames,
              locations: this.locationCodes,
              locationNames: this.locationNames,
              sets: this.setCodes,
              setNames: this.setNames,
              free_fields: this.freeFieldCodes,
              sourcecodes: this.sourceCodes,
              accounts: capitalizeFirstLetter(this.reportOptions.accountQuickPick),
              data: []
            }
          }
          // Put finished data here
          let ledgerData = []
          // TODO Use selected accounts instead of all accounts
          this.allCodes.accounts.map((data) => {
            // Place account code into report
            let account = {}
            account.account_desc = data.name
            account.account_number = data.code
            account.account_id = data.id
            account.account_parent = data.parent
            // Now look through actual data and put into the proper group
            if (sortedAccounts.current[data.code]) {
              let transactions = []
              if (this.includedTypes.includes('PR') && !this.includedTypes.includes('JE')) {
                transactions = sortedAccounts.current[data.code].filter((transactions) => this.includedTypes.includes(transactions.trans_type))
              } else {
                transactions = sortedAccounts.current[data.code]
              }
              account.account_total = this.sumRecursive(transactions, 'amount', null, null, null, false)
              account.transaction = transactions.sort((a, b) => a.trans_number.localeCompare(b.trans_number, 'en', { numeric: true }))
              ledgerData.push(account)
            }
          })
          json.report.grand_total = this.sumRecursive(ledgerData, 'account_total', null, null, null, false)
          json.report.data = ledgerData
          resolve(json)
        })
      },
      generateReport () {
        this.loading = true
        this.disableReportRun = true
        this.jeIncludedForPr = false
        if (this.includedTypes.includes('PR') && !this.includedTypes.includes('JE')) {
          this.transactionTypes.jes.include = true
          this.jeIncludedForPr = true
        }
        if (this.reportOptions.format === 'csv') {
          this.getMultiCurrencyReportData('glreport')
            .then((resSorted) => {
              if (this.jeIncludedForPr) {
                this.transactionTypes.jes.include = false
                this.jeIncludedForPr = false
              }
              this.loading = false
              this.reportData = JSON.stringify(resSorted)
              this.worksheetUpdated = false
              return this.buildGlReport(resSorted)
            })
            .then((resReport) => {
              return this.buildCsvData(resReport.report.data)
            })
            .then((resCsv) => {
              this.prettyJson = resCsv
              this.createNotification('Success', 'CSV is Ready. ', 'success', false, true)
              return true
            })
            .catch(error => {
              this.loading = false
              console.log(error)
            })
            .finally(() => {
              this.disableReportRun = false
              this.loading = false
            })
        } else {
          this.getMultiCurrencyReportData('glreport')
            .then((resSorted) => {
              if (this.jeIncludedForPr) {
                this.transactionTypes.jes.include = false
                this.jeIncludedForPr = false
              }
              this.reportData = JSON.stringify(resSorted)
              this.worksheetUpdated = false
              return this.buildGlReport(resSorted)
            })
            .then(json => {
              this.prettyJson = JSON.stringify(json, null, 2)
              return this.getJsonUrl(json)
            })
            .then(resJsonUrl => {
              // PDF version of the report
              let report = this.jasperSettings.reportServer + this.jasperSettings.reportName + '.pdf?net.sf.jasperreports.json.source=' + resJsonUrl
              // HTML version of the report
              let preview = this.jasperSettings.reportServer + this.jasperSettings.reportName + '.html?net.sf.jasperreports.json.source=' + resJsonUrl
              return this.getReportUrl(report)
            })
            .then(reportUrl => {
              this.loading = false
              this.createNotification('Success', 'Report is Ready. ', 'success', reportUrl)
            })
            .catch(error => {
              this.loading = false
              console.log(error)
            })
            .finally(() => {
              this.disableReportRun = false
              this.loading = false
            })
        }
      },

      refreshWorksheet () {
        if (this.worksheetUpdated) {
          this.buildWorkSheet()
        } else {
          this.isReady = true
          this.displayOptions.groupingLevel = 2
          let glData = {
            rows: this.workSheetData,
            displayOptions: this.displayOptions,
            reportOptions: this.reportOptions
          }
          this.iteration++
          this.tab = 'report'
          this.rowData = glData.rows

          this.isWorksheet = true
          this.isReady = false

          if (this.reportOptions.type === 'summary') {
            this.toggleCollapse()
          }
        }
      },

      buildWorkSheet () {
        this.isSaving = false
        this.isWorksheet = true
        this.tab = 'report'
        this.rowData = null
        this.iteration++
        this.isReady = true
        this.jeLinesToBeUpdated = []
        this.pcLinesToBeUpdated = []
        this.poLinesToBeUpdated = []
        this.displayOptions.groupingLevel = 2
        this.jeIncludedForPr = false
        if (this.includedTypes.includes('PR') && !this.includedTypes.includes('JE')) {
          this.transactionTypes.jes.include = true
          this.jeIncludedForPr = true
        }
        this.getMultiCurrencyReportData('glreport')
          .then((resSorted) => {
            if (this.jeIncludedForPr) {
              this.transactionTypes.jes.include = false
              this.jeIncludedForPr = false
            }
            this.reportData = JSON.stringify(resSorted)
            this.worksheetUpdated = false
            return this.buildWorkSheetReport(resSorted)
          })
          .then(json => {
            let glData = {
              rows: json,
              displayOptions: this.displayOptions,
              reportOptions: this.reportOptions
            }
            this.rowData = glData.rows
            return glData.rows
          })
          .catch(error => {
            this.isReady = false
            this.isWorksheet = false
            this.tab = 'option'
            this.rowData = null
            console.log(error)
          })
          .finally(() => {
            this.isReady = false
            if (this.reportOptions.type === 'summary') {
              this.toggleCollapse()
            }
          })
      },

      buildWorkSheetReport (sortedAccounts) {
        return new Promise((resolve, reject) => {
          let glData = []
          this.allCodes.accounts.map((data) => {
            let transactions = []
            if (sortedAccounts.current[data.code]) {
              if (this.includedTypes.includes('PR') && !this.includedTypes.includes('JE')) {
                transactions = sortedAccounts.current[data.code].filter((transactions) => this.includedTypes.includes(transactions.trans_type)).sort((a, b) => a.trans_number.localeCompare(b.trans_number, 'en', { numeric: true }))
              } else {
                transactions = sortedAccounts.current[data.code].sort((a, b) => a.trans_number.localeCompare(b.trans_number, 'en', { numeric: true }))
              }
              transactions.forEach((trn, n) => {
                trn.type = 'grand_total'
                trn.grandparent = data.code
                trn.grandparentDesc = ''
                trn.code = n + '__' + trn.trans_number
                trn.line_desc = trn.line_desc

                glData.push(trn)
              })
            }
          })
          this.originalLines = JSON.stringify(glData)
          this.workSheetData = glData
          this.getDcSettings()
          resolve(glData)
        })
      },

      toggleExpand () {
        this.gridOptions.api.expandAll(true)
        this.displayOptions.groupingLevel = 2
      },

      toggleCollapse () {
        this.gridOptions.api.forEachNode((node) => {
          if (node.level === 1) node.expanded = false
        })
        this.gridOptions.api.onGroupExpandedOrCollapsed()
        this.displayOptions.groupingLevel = 1
      },

      async saveOptions () {
        if (this.isBusy) return
        if (this.empty_error || this.delete_error) {
          this.visible = false
          this.empty_error = false
          this.delete_error = false
          return
        }
        this.isBusy = true
        if (this.selectedReportOptions) {
          await this.updateReportOptions({
            data: {
              report_group: this.report_group,
              report_name: this.selectedReportOptions.report_name,
              filter: { reportOptions: this.reportOptions, selectedCodes: this.selectedCodes, transactionTypes: this.transactionTypes }
            },
            id: this.selectedReportOptions.id })
        } else {
          if (!this.savereportoptions) {
            this.$notify({ title: 'Error', message: 'Report Name is required', type: 'error' })
            return
          }
          this.newAddedReport = await this.saveReportOptions({
            report_group: this.report_group,
            report_name: this.savereportoptions,
            filter: { reportOptions: this.reportOptions, selectedCodes: this.selectedCodes, transactionTypes: this.transactionTypes }
          })
        }
        this.visible = false
        this.$notify({ title: 'Success', message: 'Report Options Saved', type: 'success' })
        await this.getOptions()
        this.empty_error = false
        this.delete_error = false
        this.isBusy = false
        this.savereportoptions = ''
        this.selectedOption = this.newAddedReport.id
      },
      openSaveOption () {
        this.visible = true
      },
      async getOptions () {
        try {
          const savedReportOptions = await this.getReportOptions()
          this.savedReportOptions = savedReportOptions.results
        } catch (e) {
          console.log(e)
        }
      },
      async loadOptions () {
        if (this.selectedReportOptions && this.selectedReportOptions.id) {
          const loadReportOptions = await this.getReportOptions(this.selectedReportOptions.id)
          const { filter: { reportOptions, selectedCodes, transactionTypes } } = loadReportOptions

          this.reportOptions = reportOptions
          setTimeout(() => {
            this.selectedCodes = selectedCodes
            this.transactionTypes = transactionTypes
            this.reRender++
          }, 10)
        } else {
          this.empty_error = true
          this.visible = true
        }
      },
      async deleteOptions () {
        try {
          if (this.selectedReportOptions) {
            this.visible_delete = true
          } else {
            this.delete_error = true
            this.visible = true
          }
        } catch (e) {
          console.log(e)
        }
      },
      async confirmDeleteOptions () {
        try {
          await this.deleteReportOptions(this.selectedReportOptions.id)
          this.savedReportOptions = this.savedReportOptions.filter(options => options.id !== this.selectedReportOptions.id)
          this.selectedReportOptions = null
          this.selectedOption = null
          this.resetFilters()
          this.getOptions()
          this.$notify({ title: 'Success', message: 'Report Options Deleted', type: 'success' })
        } catch (e) {
          console.log(e)
        }
        this.visible_delete = false
      },
      handleSelection (value) {
        this.selectedReportOptions = value ? this.savedReportOptions.find((s) => s.id === value) : null
        if (!value) this.resetFilters()
      },
      resetFilters () {
        this.reportOptions = {
          transactionStatuses: [ 'posted', 'closed' ],
          accountQuickPick: 'all',
          vendorQuickPick: 'all',
          type: 'detail',
          dates: 'datesAll',
          sort: 'account',
          status: 'all',
          format: 'pdf',
          currencyFactor: undefined,
          report_currency_id: 1,
          includeunposted: false
        }
        this.selectedCodes = {
          account_id1: [],
          account_id2: [],
          period_start: undefined,
          period_end: undefined,
          time_posted_start: undefined,
          time_posted_end: undefined,
          effective_date_start: undefined,
          effective_date_end: undefined,
          account_id: [],
          series_id: [],
          location_id: [],
          set_id: [],
          free_field_1_id: [],
          free_field_2_id: [],
          free_field_3_id: [],
          free_field_4_id: [],
          company_id: undefined,
          production_id: [],
          source_code_id: [],
          asset: undefined,
          currency_id: [],
          vendor_id: []
        }
        this.transactionTypes = {     // Types of transactions and whether we need them or not
          'aps': { name: 'AP', include: true },
          'jes': { name: 'JE', include: true },
          'pcs': { name: 'PC', include: true },
          'prs': { name: 'PR', include: true },
          'cks': { name: 'CK', include: true },
          'pos': { name: 'PO', include: false },
          'efts': { name: 'EFT', include: true },
          'wts': { name: 'WIRE', include: true },
          'mcs': { name: 'MC', include: true }
        }
      },
      // Grid Methods
      onGridReady (params) {
        if (params) {
          this.gridApi = params.api
          this.columnApi = params.columnApi
        }
      },
      onFirstDataRendered (params) {
        this.onChangeVisibleColumns()
        params.api.sizeColumnsToFit()
        this.isLoading = false
      },
      async onCellValueChanged (params) {
        // Ignore if not changed
        if ((params.oldValue === params.newValue) || (params.oldValue === null && params.newValue === undefined)) return

        this.hasUpdateErrors = false
        let transactionType = params.data.trans_type

        if (transactionType === 'PC') {
          const isPcExisted = this.pcLinesToBeUpdated.find(line => Number(line.id) === Number(params.data.line_id))
          if (!isPcExisted) {
            const results = await this.$store.dispatch('fetchLinesWithQuery', { type: 'pc', query: { id: params.data.line_id } })
            if (results && results.count && Object.values(results.entities.lines)) {
              Object.values(results.entities.lines).forEach((line) => {
                this.pcLinesToBeUpdated.push(line)
              })
            }
          }
        }

        if (params.colDef.colId) {
          let jeLineIndex = this.jeLinesToBeUpdated.findIndex(v => v.id === params.data.line_id && Number(v.dcLineId) === Number(params.data.dc_line_id))
          let poLineIndex = this.poLinesToBeUpdated.findIndex(v => v.id === params.data.line_id && Number(v.dcLineId) === Number(params.data.dc_line_id))
          switch (params.colDef.colId) {
            case 'account':
              let account = this.allCodes.accounts.find(v => v.code === params.newValue && v.postable)
              if (!account) {
                this.hasUpdateErrors = true
                this.$notify({ title: 'Error', message: 'Invalid account', type: 'error' })
                return
              }

              if (poLineIndex > -1) {
                this.poLinesToBeUpdated[poLineIndex]['account'] = account.id
              } else if (jeLineIndex > -1) {
                this.jeLinesToBeUpdated[jeLineIndex]['account'] = account.id
              } else {
                const item = {
                  id: params.data.line_id,
                  account: account.id,
                  dcLineId: Number(params.data.dc_line_id),
                  trnType: params.data.trans_type,
                  trnId: params.data.subledger_id,
                  trnLedgerId: params.data.transaction_ledger_id
                }
                if (transactionType === 'PO') {
                  this.poLinesToBeUpdated.push(item)
                } else {
                  this.jeLinesToBeUpdated.push(item)
                }
              }
              break
            case 'set':
              let set = this.allCodes.sets.find(v => v.code === params.newValue)
              if (!set) {
                this.hasUpdateErrors = true
                this.$notify({ title: 'Error', message: 'Invalid set', type: 'error' })
                return
              }

              if (poLineIndex > -1) {
                this.poLinesToBeUpdated[poLineIndex]['set'] = set.id
              } else if (jeLineIndex > -1) {
                this.jeLinesToBeUpdated[jeLineIndex]['set'] = set.id
              } else {
                const item = {
                  id: params.data.line_id,
                  set: set.id,
                  dcLineId: Number(params.data.dc_line_id),
                  trnType: params.data.trans_type,
                  trnId: params.data.subledger_id,
                  trnLedgerId: params.data.transaction_ledger_id
                }
                if (transactionType === 'PO') {
                  this.poLinesToBeUpdated.push(item)
                } else {
                  this.jeLinesToBeUpdated.push(item)
                }
              }
              break
            case 'location':
              let location = this.allCodes.locations.find(v => v.code === params.newValue)
              if (!location) {
                this.hasUpdateErrors = true
                this.$notify({ title: 'Error', message: 'Invalid location', type: 'error' })
                return
              }

              if (poLineIndex > -1) {
                this.poLinesToBeUpdated[poLineIndex]['location'] = location.id
              } else if (jeLineIndex > -1) {
                this.jeLinesToBeUpdated[jeLineIndex]['location'] = location.id
              } else {
                const item = {
                  id: params.data.line_id,
                  location: location.id,
                  dcLineId: Number(params.data.dc_line_id),
                  trnType: params.data.trans_type,
                  trnId: params.data.subledger_id,
                  trnLedgerId: params.data.transaction_ledger_id
                }
                if (transactionType === 'PO') {
                  this.poLinesToBeUpdated.push(item)
                } else {
                  this.jeLinesToBeUpdated.push(item)
                }
              }
              break
            case 'series':
              let series = this.allCodes.series.find(v => v.code === params.newValue)
              if (!series) {
                this.hasUpdateErrors = true
                this.$notify({ title: 'Error', message: 'Invalid series', type: 'error' })
                return
              }
              if (poLineIndex > -1) {
                this.poLinesToBeUpdated[poLineIndex]['series'] = series.id
              } else if (jeLineIndex > -1) {
                this.jeLinesToBeUpdated[jeLineIndex]['series'] = series.id
              } else {
                const item = {
                  id: params.data.line_id,
                  series: series.id,
                  dcLineId: Number(params.data.dc_line_id),
                  trnType: params.data.trans_type,
                  trnId: params.data.subledger_id,
                  trnLedgerId: params.data.transaction_ledger_id
                }
                if (transactionType === 'PO') {
                  this.poLinesToBeUpdated.push(item)
                } else {
                  this.jeLinesToBeUpdated.push(item)
                }
              }
              break
            case 'tax_code':
              let taxcode = null
              if (params.newValue) {
                taxcode = this.allCodes.taxcodes.find(v => v.code === params.newValue)
                if (!taxcode) {
                  this.hasUpdateErrors = true
                  this.$notify({ title: 'Error', message: 'Invalid tax code', type: 'error' })
                  return
                } else {
                  taxcode = taxcode.id
                }
              }
              if (poLineIndex > -1) {
                this.poLinesToBeUpdated[poLineIndex]['tax_code'] = taxcode
              } else if (jeLineIndex > -1) {
                this.jeLinesToBeUpdated[jeLineIndex]['tax_code'] = taxcode
              } else {
                const item = {
                  id: params.data.line_id,
                  tax_code: taxcode,
                  dcLineId: Number(params.data.dc_line_id),
                  trnType: params.data.trans_type,
                  trnId: params.data.subledger_id,
                  trnLedgerId: params.data.transaction_ledger_id
                }
                if (transactionType === 'PO') {
                  this.poLinesToBeUpdated.push(item)
                } else {
                  this.jeLinesToBeUpdated.push(item)
                }
              }
              break
            case 'free_field_1':
            case 'free_field_2':
            case 'free_field_3':
            case 'free_field_4':
              let freeField = null
              if (params.newValue) {
                freeField = this.allCodes.freefield1.find(v => v.code === params.newValue)
                if (!freeField) {
                  this.hasUpdateErrors = true
                  this.$notify({ title: 'Error', message: 'Invalid Free field', type: 'error' })
                  return
                } else {
                  freeField = freeField.id
                }
              }
              if (poLineIndex > -1) {
                this.poLinesToBeUpdated[poLineIndex][params.colDef.colId] = freeField
              } else if (jeLineIndex > -1) {
                this.jeLinesToBeUpdated[jeLineIndex][params.colDef.colId] = freeField
              } else {
                let item = {
                  id: params.data.line_id,
                  dcLineId: Number(params.data.dc_line_id),
                  trnType: params.data.trans_type,
                  trnId: params.data.subledger_id,
                  trnLedgerId: params.data.transaction_ledger_id
                }
                item[params.colDef.colId] = freeField
                if (transactionType === 'PO') {
                  this.poLinesToBeUpdated.push(item)
                } else {
                  this.jeLinesToBeUpdated.push(item)
                }
              }
              break
            case 'line_desc':
              if (poLineIndex > -1) {
                this.poLinesToBeUpdated[poLineIndex]['description'] = params.newValue
              } else if (jeLineIndex > -1) {
                this.jeLinesToBeUpdated[jeLineIndex]['description'] = params.newValue
              } else {
                const item = {
                  id: params.data.line_id,
                  description: params.newValue,
                  dcLineId: Number(params.data.dc_line_id),
                  trnType: params.data.trans_type,
                  trnId: params.data.subledger_id,
                  trnLedgerId: params.data.transaction_ledger_id
                }
                if (transactionType === 'PO') {
                  this.poLinesToBeUpdated.push(item)
                } else {
                  this.jeLinesToBeUpdated.push(item)
                }
              }
              break
            case 'asset':
              if (poLineIndex > -1) {
                this.poLinesToBeUpdated[poLineIndex]['asset'] = params.newValue
              } else if (jeLineIndex > -1) {
                this.jeLinesToBeUpdated[jeLineIndex]['asset'] = params.newValue
              } else {
                const item = {
                  id: params.data.line_id,
                  asset: params.newValue,
                  dcLineId: Number(params.data.dc_line_id),
                  trnType: params.data.trans_type,
                  trnId: params.data.subledger_id,
                  trnLedgerId: params.data.transaction_ledger_id
                }
                if (transactionType === 'PO') {
                  this.poLinesToBeUpdated.push(item)
                } else {
                  this.jeLinesToBeUpdated.push(item)
                }
              }
              break
            case 'doc_number':
              if (poLineIndex > -1) {
                this.poLinesToBeUpdated[poLineIndex]['doc_number'] = params.newValue
              } else if (jeLineIndex > -1) {
                this.jeLinesToBeUpdated[jeLineIndex]['doc_number'] = params.newValue
              } else {
                const item = {
                  id: params.data.line_id,
                  doc_number: params.newValue,
                  dcLineId: Number(params.data.dc_line_id),
                  trnType: params.data.trans_type,
                  trnId: params.data.subledger_id,
                  trnLedgerId: params.data.transaction_ledger_id
                }
                if (transactionType === 'PO') {
                  this.poLinesToBeUpdated.push(item)
                } else {
                  this.jeLinesToBeUpdated.push(item)
                }
              }
              break
            default:
              if (poLineIndex > -1) {
                this.poLinesToBeUpdated[poLineIndex][params.colDef.colId] = params.newValue
              } else if (jeLineIndex > -1) {
                this.jeLinesToBeUpdated[jeLineIndex][params.colDef.colId] = params.newValue
              } else {
                let item = {
                  id: params.data.line_id,
                  dcLineId: Number(params.data.dc_line_id),
                  trnType: params.data.trans_type,
                  trnId: params.data.subledger_id,
                  trnLedgerId: params.data.transaction_ledger_id
                }
                item[params.colDef.colId] = params.newValue
                if (transactionType === 'PO') {
                  this.poLinesToBeUpdated.push(item)
                } else {
                  this.jeLinesToBeUpdated.push(item)
                }
              }
              break
          }
        }
      },
      buildOffsetLine (newLine, existingLine, lineNumber) {
        const metaData = {
          original_line: existingLine.line_id,
          original_line_type: this.getJeLineType(existingLine.trans_type)
        }
        return {
          account: newLine.account ? newLine.account : existingLine.account_id,
          amount_decimal: existingLine.amount_native,
          description: newLine.description ? newLine.description : existingLine.line_desc,
          other_name: existingLine.other_name || '',
          location: newLine.location ? newLine.location : existingLine.location_id,
          series: newLine.series ? newLine.series : existingLine.series_id,
          set: newLine.set ? newLine.set : existingLine.set_id,
          tax_code: newLine.hasOwnProperty('tax_code') ? newLine.tax_code : existingLine.tax_code_id,
          free_field_1: newLine.hasOwnProperty('free_field_1') ? newLine.free_field_1 : existingLine.free_field_1_id,
          free_field_2: newLine.hasOwnProperty('free_field_2') ? newLine.free_field_2 : existingLine.free_field_2_id,
          free_field_3: newLine.hasOwnProperty('free_field_3') ? newLine.free_field_3 : existingLine.free_field_3_id,
          free_field_4: newLine.hasOwnProperty('free_field_4') ? newLine.free_field_4 : existingLine.free_field_4_id,
          asset: newLine.asset ? Boolean(newLine.asset) : Boolean(existingLine.asset),
          third_party_vendor: existingLine.third_party_vendor_id || '',
          line_number: lineNumber,
          meta_data: metaData
        }
      },
      buildOffsettingLine (originalLine, lineNumber) {
        const metaData = {
          original_line: originalLine.line_id,
          original_line_type: this.getJeLineType(originalLine.trans_type),
          offset_attempt: true
        }
        return {
          account: originalLine.account_id,
          amount_decimal: (originalLine.amount_native * -1).toString(),
          description: originalLine.line_desc,
          other_name: originalLine.other_name || '',
          location: originalLine.location_id ? originalLine.location_id : this.allCodes.locations.find(v => v.active).id,
          series: originalLine.series_id ? originalLine.series_id : this.allCodes.series.find(v => v.active).id,
          set: originalLine.set_id ? originalLine.set_id : this.allCodes.sets.find(v => v.active).id,
          tax_code: originalLine.tax_code_id,
          free_field_1: originalLine.free_field_1_id,
          free_field_2: originalLine.free_field_2_id,
          free_field_3: originalLine.free_field_3_id,
          free_field_4: originalLine.free_field_4_id,
          asset: Boolean(originalLine.asset),
          third_party_vendor: originalLine.third_party_vendor_id || '',
          line_number: lineNumber,
          meta_data: metaData
        }
      },
      buildExistingOffsetLine (line, lineNumber) {
        return {
          account: line.account,
          amount_decimal: line.amount_decimal,
          description: line.description,
          other_name: line.other_name || '',
          location: line.location,
          series: line.series,
          set: line.set,
          tax_code: line.tax_code,
          free_field_1: line.free_field_1,
          free_field_2: line.free_field_2,
          free_field_3: line.free_field_3,
          free_field_4: line.free_field_4,
          asset: Boolean(line.asset),
          third_party_vendor: line.third_party_vendor || '',
          line_number: lineNumber,
          meta_data: line.meta_data
        }
      },
      getJeLineType (trnType) {
        let lineType = ''
        switch (trnType) {
          case 'JE':
          case 'PR':
            lineType = 'journalentryline'
            break
          case 'AP':
            lineType = 'accountspayableline'
            break
          case 'PC':
            lineType = 'pettycashline'
            break
          case 'PO':
            lineType = 'purchaseorderline'
            break
        }
        return lineType
      },
      buildJeHeader (existingJe) {
        return {
          batch: existingJe.batch,
          period: existingJe.period || null,
          description: existingJe.description,
          number: existingJe.number,
          debits_decimal: existingJe.debits_decimal,
          credits_decimal: existingJe.credits_decimal,
          original_trx_number: existingJe.original_trx_number,
          effective_date: moment(new Date()).format('YYYY-MM-DD'),
          date: moment(new Date()).format('YYYY-MM-DD'),
          source_code: this.allCodes.sourcecodes.find(v => v.code === 'DC').id || '',
          lines: []
        }
      },
      buildDcJeHeader (offsetJeLines, existingLine) {
        const debits = offsetJeLines.reduce((a, b) => a + (parseFloat(b.amount_decimal) > 0 ? parseFloat(b.amount_decimal) : 0), 0).toFixed(2)
        const credits = offsetJeLines.reduce((a, b) => a + (parseFloat(b.amount_decimal) < 0 ? parseFloat(b.amount_decimal) : 0), 0).toFixed(2)
        return {
          batch: existingLine.transaction_batch_id,
          period: existingLine.transaction_period_id || null,
          description: `DC for ${existingLine.transaction_ledger_id}`,
          number: existingLine.doc_number,
          debits_decimal: debits,
          credits_decimal: credits,
          original_trx_number: existingLine.transaction_ledger_id,
          effective_date: moment(new Date()).format('YYYY-MM-DD'),
          date: moment(new Date()).format('YYYY-MM-DD'),
          source_code: this.allCodes.sourcecodes.find(v => v.code === 'DC').id || '',
          lines: []
        }
      },
      isExternalFilterPresent () {
        return this.$data.displayOptions.hideZeroRows
      },
      doesExternalFilterPass (node) {
        if (!node || !node.data) {
          return false
        } else {
          return true
        }
      },
      onExport () {
        this.gridApi.exportDataAsExcel()
      },
      onChangeVisibleColumns (column = null) {
        if (column) {
          this.$nextTick(() => {
            this.columnApi.setColumnVisible(column, this.displayOptions.columns.find((v) => { return v.name === column }).display)
            this.gridApi.sizeColumnsToFit()
          })
        } else {
          this.$nextTick(() => {
            this.displayOptions.columns.forEach(col => {
              this.columnApi.setColumnVisible(col.name, col.display)
            })
            this.gridApi.sizeColumnsToFit()
          })
        }
      },
      printPdf (type) {
        this.isPrinting = true
        if (this.jeLinesToBeUpdated.length || this.poLinesToBeUpdated.length) {
          this.$notify({ title: 'Error', message: 'Please save your changes before printing.', type: 'error' })
          this.isPrinting = false
          return
        }
        this.isPrinted = true
        this.reportOptions.type = type
        this.jasperSettings.reportName = 'general_ledger_' + type
        if (this.worksheetUpdated) {
          this.jeIncludedForPr = false
          if (this.includedTypes.includes('PR') && !this.includedTypes.includes('JE')) {
            this.transactionTypes.jes.include = true
            this.jeIncludedForPr = true
          }
          this.getMultiCurrencyReportData('glreport')
            .then((resSorted) => {
              if (this.jeIncludedForPr) {
                this.transactionTypes.jes.include = false
                this.jeIncludedForPr = false
              }
              this.reportData = JSON.stringify(resSorted)
              this.worksheetUpdated = false
              return this.buildGlReport(resSorted)
            })
            .then(json => {
              this.prettyJson = JSON.stringify(json, null, 2)
              return this.getJsonUrl(json)
            })
            .then(resJsonUrl => {
              let report = this.jasperSettings.reportServer + this.jasperSettings.reportName + '.pdf?net.sf.jasperreports.json.source=' + resJsonUrl
              return this.getReportUrl(report)
            })
            .then(reportUrl => {
              this.pdfData = reportUrl
              this.tab = 'viewer'
              this.isPrinting = false
            })
        } else {
          this.buildGlReport(JSON.parse(this.reportData))
            .then(json => {
              this.prettyJson = JSON.stringify(json, null, 2)
              return this.getJsonUrl(json)
            })
            .then(resJsonUrl => {
              let report = this.jasperSettings.reportServer + this.jasperSettings.reportName + '.pdf?net.sf.jasperreports.json.source=' + resJsonUrl
              return this.getReportUrl(report)
            })
            .then(reportUrl => {
              this.pdfData = reportUrl
              this.tab = 'viewer'
              this.isPrinting = false
            })
        }
      },
      async jesToBeCreated () {
        let jes = []
        // VX source code id
        const voidSourceCode = this.allCodes.sourcecodes.find(v => v.code === 'VX').id || ''
        // DC source code id
        const dcSourceCode = this.allCodes.sourcecodes.find(v => v.code === 'DC').id || ''
        // Unique transactions of updated lines
        const trns = [...new Set(this.jeLinesToBeUpdated.map((item) => item.trnLedgerId))]
        // line ids of updated lines
        const editedLineIds = this.jeLinesToBeUpdated.map((item) => item.id)
        // Original lines to be parsed
        const originalTrnLines = JSON.parse(this.originalLines)

        // Loop each transactions. Find Jes if already created else create Je
        for await (const trn of trns) {
          let je = { header: {}, lines: [] }
          let dcUpdatedDate = ''
          // Get existing Je
          const dcJes = await this.$store.dispatch('fetchJesWithQuery', { source_code: dcSourceCode, original_trx_number: trn, status__in: ['POSTED', 'CLOSED'] })
          if (dcJes && dcJes.results.length) {
            let updatedLines = []
            const dcJesData = dcJes.results.sort((a, b) => a.id - b.id)
            for await (const dcJe of dcJesData) {
              if (dcUpdatedDate) {
                if (new Date(dcUpdatedDate) <= new Date(dcJe.updated)) {
                  dcUpdatedDate = dcJe.updated
                  je.header = this.buildJeHeader(dcJe)
                }
              } else {
                dcUpdatedDate = dcJe.updated
                je.header = this.buildJeHeader(dcJe)
              }
              // Get Je lines
              const results = await this.$store.dispatch('fetchLinesWithQuery', { type: 'je', query: { transaction__id: dcJe.id } })
              if (results && results.count && Object.values(results.entities.lines)) {
                const dcJeLines = Object.values(results.entities.lines).filter((v) => v.meta_data !== null)
                // Check Je lines are in edited lines
                let dcJeLinesEdited = []
                editedLineIds.forEach((id) => {
                  if (dcJeLines.find((v) => Number(v.meta_data.original_line) === Number(id))) {
                    dcJeLinesEdited.push(dcJeLines.find((v) => Number(v.meta_data.original_line) === Number(id)))
                    updatedLines.push(id)
                  }
                })
                // If any of Je lines edited
                if (dcJeLinesEdited && dcJeLinesEdited.length) {
                  // Filter Offset lines
                  let dcJeLinesEditedOriginal = dcJeLines.filter((v) => !v.offset_by)
                  // Loop Je offset lines
                  dcJeLinesEditedOriginal.forEach((dcJeLine) => {
                    // Find edited line
                    const editedLine = this.jeLinesToBeUpdated.find((v) => v.id === dcJeLine.meta_data.original_line && v.dcLineId === dcJeLine.id)
                    let lineNumber = je.lines.length
                    if (editedLine && editedLine.id) {
                      // Offsetting Line
                      const originalOffsettingLine = dcJeLines.find((v) => v.offset_by === dcJeLine.id)
                      if (dcJeLine.other_name) {
                        originalOffsettingLine.other_name = dcJeLine.other_name
                      }
                      if (originalOffsettingLine && originalOffsettingLine.account) {
                        lineNumber++
                        const offsettingLine = this.buildExistingOffsetLine(originalOffsettingLine, lineNumber)
                        je.lines.push(offsettingLine)
                      }
                      // Offset Line
                      const originalLine = originalTrnLines.find((v) => v.line_id === editedLine.id && Number(v.dc_line_id) === editedLine.dcLineId && v.transaction_ledger_id === editedLine.trnLedgerId)
                      if (originalLine && originalLine.line_id) {
                        if (originalLine.source_code === 'PC') {
                          if (dcJeLine.other_name) {
                            originalLine.other_name = dcJeLine.other_name
                          } else {
                            const pcLine = this.pcLinesToBeUpdated.find(line => line.id === originalLine.line_id)
                            if (pcLine && pcLine.other_name) {
                              originalLine.other_name = pcLine.other_name
                            }
                          }
                        }
                        lineNumber++
                        const offsetLine = this.buildOffsetLine(editedLine, originalLine, lineNumber)
                        je.lines.push(offsetLine)
                      }
                    } else { // create new Je line with existing Je line
                      lineNumber++
                      // Offset Line
                      const offsetLine = this.buildExistingOffsetLine(dcJeLine, lineNumber)
                      je.lines.push(offsetLine)
                      // Offsetting Line
                      const originalOffsettingLine = dcJeLines.find((v) => v.offset_by === dcJeLine.id)
                      if (originalOffsettingLine && originalOffsettingLine.account) {
                        lineNumber++
                        const offsettingLine = this.buildExistingOffsetLine(originalOffsettingLine, lineNumber)
                        je.lines.push(offsettingLine)
                      }
                    }
                  })

                  // VOID JE Transaction
                  this.$store.dispatch('updateJe', { id: dcJe.id, data: { status: 'VOIDED' } })
                    .then((jeObj) => {
                      // fetch the "voiding" (VX) transactions
                      if (jeObj && jeObj.ledger) {
                        this.$store.dispatch('fetchJesWithQuery', { nest_lines: false, source_code: voidSourceCode, original_trx_number: jeObj.ledger })
                          .then((vxJes) => {
                            if (vxJes && vxJes.results.length) { // Update JE Status to POSTED
                              for (const res of vxJes.results) {
                                this.$store.dispatch('updateJe', { id: res.id, data: { status: 'POSTED' } })
                              }
                            }
                          })
                          .catch(error => {
                            console.log(error)
                          })
                      }
                    })
                    .catch(error => {
                      console.log(error)
                    })
                }
              }
            }
            // Lines are edited but no dcs
            if (updatedLines.length) {
              editedLineIds.forEach((id) => {
                if (!updatedLines.includes(id)) {
                  const trnLine = this.jeLinesToBeUpdated.find((item) => Number(item.id) === Number(id) && item.trnLedgerId === trn)
                  if (trnLine && trnLine.id) {
                    // Create if there is no DC line
                    let lineNumber = je.lines.length
                    const originalLine = originalTrnLines.find((v) => Number(v.line_id) === Number(id) && v.transaction_ledger_id === trnLine.trnLedgerId)

                    if (originalLine && originalLine.account_id) {
                      if (originalLine.source_code === 'PC') {
                        const pcLine = this.pcLinesToBeUpdated.find(line => line.id === originalLine.line_id)
                        if (pcLine && pcLine.other_name) {
                          originalLine.other_name = pcLine.other_name
                        }
                      }
                      // Offset Line
                      lineNumber++
                      const offsetLine = this.buildOffsetLine(trnLine, originalLine, lineNumber)
                      je.lines.push(offsetLine)
                      lineNumber++
                      // Offsetting Line
                      const offsettingLine = this.buildOffsettingLine(originalLine, lineNumber)
                      je.lines.push(offsettingLine)
                      updatedLines.push(id)
                    }
                  }
                }
              })
            } else if (je.lines.length === 0) {
              const trnLines = this.jeLinesToBeUpdated.filter((item) => item.trnLedgerId === trn)
              let jeLines = []
              trnLines.forEach((trnLine, tln) => {
                // Create if there is no DC line
                if (!je.lines.filter((v) => { return [trnLine.id].includes(v.meta_data.original_line) }).length) {
                  let lineNumber = jeLines.length
                  const originalLine = originalTrnLines.find((v) => v.line_id === trnLine.id && v.transaction_ledger_id === trnLine.trnLedgerId)

                  if (originalLine && originalLine.account_id) {
                    if (originalLine.source_code === 'PC') {
                      const pcLine = this.pcLinesToBeUpdated.find(line => line.id === originalLine.line_id)
                      if (pcLine && pcLine.other_name) {
                        originalLine.other_name = pcLine.other_name
                      }
                    }
                    // Offset Line
                    lineNumber++
                    const offsetLine = this.buildOffsetLine(trnLine, originalLine, lineNumber)
                    jeLines.push(offsetLine)
                    lineNumber++
                    // Offsetting Line
                    const offsettingLine = this.buildOffsettingLine(originalLine, lineNumber)
                    jeLines.push(offsettingLine)
                  }

                  if (trnLines.length === (tln + 1)) {
                    je.header = this.buildDcJeHeader(jeLines, originalLine)
                    je.lines = jeLines
                  }
                }
              })
            }
          } else {
            const trnLines = this.jeLinesToBeUpdated.filter((item) => item.trnLedgerId === trn)
            const allowDC = this.canCreateDc(trnLines)
            if (allowDC) { // Create DC JE
              let jeLines = []
              trnLines.forEach((trnLine, tln) => {
                let lineNumber = jeLines.length
                const originalLine = originalTrnLines.find((v) => v.line_id === trnLine.id && v.transaction_ledger_id === trnLine.trnLedgerId)
                if (originalLine && originalLine.account_id) {
                  if (originalLine.source_code === 'PC') {
                    const pcLine = this.pcLinesToBeUpdated.find(line => line.id === originalLine.line_id)
                    if (pcLine && pcLine.other_name) {
                      originalLine.other_name = pcLine.other_name
                    }
                  }
                  // Offset Line
                  lineNumber++
                  const offsetLine = this.buildOffsetLine(trnLine, originalLine, lineNumber)
                  jeLines.push(offsetLine)
                  lineNumber++
                  // Offsetting Line
                  const offsettingLine = this.buildOffsettingLine(originalLine, lineNumber)
                  jeLines.push(offsettingLine)
                }

                if (trnLines.length === (tln + 1)) {
                  je.header = this.buildDcJeHeader(jeLines, originalLine)
                  je.lines = jeLines
                }
              })
            } else { // Update transaction lines
              const trnId = trnLines[0].trnId
              let trnType = (trnLines[0].trnType).toLowerCase()
              if (trnType === 'pr') trnType = 'je'
              let linesToBeUpdated = []
              trnLines.forEach((trnLine, k) => {
                linesToBeUpdated[k] = {}
                let updatedColumns = Object.keys(trnLine).filter(x => !['dcLineId', 'trnType', 'trnId', 'trnLedgerId'].includes(x))
                updatedColumns.forEach(updatedColumn => {
                  linesToBeUpdated[k][updatedColumn] = trnLine[updatedColumn]
                })
                if (trnLines.length === (k + 1)) {
                  this.$store.dispatch(`updateTransactionLine`, { id: trnId, type: trnType, data: linesToBeUpdated })
                }
              })
            }
          }
          if (je.header && je.lines.length) {
            je.header.debits_decimal = je.lines.reduce((a, b) => a + (parseFloat(b.amount_decimal) > 0 ? parseFloat(b.amount_decimal) : 0), 0).toFixed(2)
            je.header.credits_decimal = je.lines.reduce((a, b) => a + (parseFloat(b.amount_decimal) < 0 ? parseFloat(b.amount_decimal) : 0), 0).toFixed(2)
            jes.push(je)
          }
        }
        return jes
      },
      canCreateDc (lines) {
        // No DC Settings
        if (!this.dcColumns.length && !this.dcAllowedColumns.length) return true
        // No columns with Y from DC Settings
        if (this.dcColumns.length && !this.dcAllowedColumns.length) return false

        return Object.values(lines).filter(v => {
          let updatedColumns = Object.keys(v).filter(x => !['id', 'dcLineId', 'trnType', 'trnId', 'trnLedgerId'].includes(x))
          return updatedColumns.some(updatedColumn => {
            return this.dcAllowedColumns.includes(this.dcAllowedColumnMap[updatedColumn])
          })
        }).length
      },
      preparePoTransactions () {
        return new Promise((resolve, reject) => {
          let poTransactions = []
          this.poLinesToBeUpdated.forEach((poLine) => {
            const existingLine = this.workSheetData.find(v => v.line_id === poLine.id)
            const transactionId = existingLine.subledger_id ? existingLine.subledger_id : existingLine.trans_number
            const transactionStatus = existingLine.transaction_status ? existingLine.transaction_status : 'POSTED'
            const poTrxIndex = poTransactions.findIndex(v => v.id === transactionId)

            const poLineItem = {
              account: poLine.account || existingLine.account_id,
              amount_decimal: existingLine.amount_native,
              asset: poLine.asset || existingLine.asset,
              description: poLine.description || existingLine.line_desc,
              free_field_1: poLine.free_field_1 || existingLine.free_field_1_id,
              free_field_2: poLine.free_field_2 || existingLine.free_field_2_id,
              free_field_3: poLine.free_field_3 || existingLine.free_field_3_id,
              free_field_4: poLine.free_field_4 || existingLine.free_field_4_id,
              id: poLine.id || existingLine.line_id,
              line_number: existingLine.line_number,
              location: poLine.location || existingLine.location_id,
              other_name: existingLine.other_name || '',
              series: poLine.series || existingLine.series_id,
              set: poLine.set || existingLine.set_id,
              tax_code: poLine.tax_code || existingLine.tax_code_id,
              third_party_vendor: poLine.third_party_vendor || existingLine.third_party_vendor_id,
              transaction: transactionId,
              type: existingLine.line_type
            }
            if (poTrxIndex > -1) {
              poTransactions[poTrxIndex].data.lines.push(poLineItem)
            } else {
              poTransactions.push({
                id: transactionId,
                data: {
                  lines: [poLineItem],
                  status: transactionStatus
                }
              })
            }
          })
          resolve(poTransactions)
        })
      },
      async saveChanges (e) {
        this.isSaving = true
        if (this.hasUpdateErrors) {
          this.$notify({ title: 'Warning', message: 'No changes to save', type: 'warning' })
          this.isSaving = false
          return
        }
        if (!this.jeLinesToBeUpdated.length && !this.poLinesToBeUpdated.length) {
          this.$notify({ title: 'Warning', message: 'No changes to save', type: 'warning' })
          this.isSaving = false
          return
        }
        try {
          // Update PO transaction lines
          if (this.poLinesToBeUpdated.length) {
            let poTransactions = await this.preparePoTransactions()
            for (const [p, poTransaction] of poTransactions.entries()) {
              this.$store.dispatch('updatePo', poTransaction)
              if (poTransactions.length === (p + 1) && !this.jeLinesToBeUpdated.length) {
                this.resetChanges()
              }
            }
          }

          // Create new JEs for distribution line changes
          if (this.jeLinesToBeUpdated.length) {
            let transactions = await this.jesToBeCreated()
            for (const [key, transaction] of transactions.entries()) {
              const jeHeader = transaction.header
              const lines = transaction.lines
              this.$store.dispatch('saveJe', {...jeHeader, lines}) // Create JE for DC changes
                .then((response) => {
                  if (response && response.id) { // Update JE Status to POSTED
                    this.$store.dispatch('updateJe', { id: response.id, data: { status: 'POSTED' } })
                      .then(() => {
                        if (transactions.length === (key + 1)) {
                          this.resetChanges()
                        }
                      })
                      .catch(error => {
                        console.log(error)
                      })
                  }
                })
                .catch(error => {
                  console.log(error)
                })
            }
            if (!transactions.length) this.resetChanges()
          }
        } catch (error) {
          this.isSaving = false
          console.log(error)
          return false
        }
      },
      resetChanges () {
        this.worksheetUpdated = true
        this.jeLinesToBeUpdated = []
        this.poLinesToBeUpdated = []
        this.pcLinesToBeUpdated = []
        this.dcColumns = []
        this.dcAllowedColumns = []
        this.originalLines = ''
        this.$notify({ title: 'Success', message: 'Changes saved successfully', type: 'success' })
        this.rowData = null
        this.isSaving = false
        this.refreshWorksheet()
      },
      buildTransData (paramsData) {
        if (paramsData.trans_type === 'PO') {
          return paramsData.trans_number
        } else {
          return `${paramsData.trans_type} ${paramsData.trans_number}`
        }
      },
      async getAppSettings () {
        const entryOptions = this.$store.getters.getUiSettings['TRANSACTION_ENTRY_OPTIONS']
        if (entryOptions && JSON.parse(entryOptions)) {
          this.effectiveDateMandatory = JSON.parse(entryOptions).effective_date
        }
      },
      async getDcSettings () {
        this.dcColumns = []
        this.dcAllowedColumns = []
        let response = await this.$store.dispatch('fetchUiSetting', { name: 'DC_SETTINGS' })
        if (response && response.value) {
          const dcSettings = JSON.parse(response.value)
          this.dcColumns = Object.keys(dcSettings)
          this.dcAllowedColumns = this.dcColumns.filter(x => dcSettings[x] === 'Y')
        }
      }
    },
    watch: {
      hideDecimals (newValue) {
        this.gridApi.redrawRows()
      }
    },
    beforeMount () {
      this.gridOptions = {
        singleClickEdit: false,
        undoRedoCellEditing: true,
        enterMovesDown: true,
        enterMovesDownAfterEdit: true,
        rowSelection: 'multiple',
        defaultColDef: {
          filter: 'agTextColumnFilter',
          floatingFilter: true,
          floatingFilterComponentParams: {
            suppressFilterButton: true
          },
          resizable: true
        },
        // Group Related
        groupDefaultExpanded: this.displayOptions.groupingLevel,
        suppressAggFuncInHeader: false,
        groupRemoveLowestSingleChildren: true,
        pinnedBottomRowData: [],
        treeData: true,
        getDataPath: (data) => {
          let accountHierarchy = []
          if (data.type) {
            let prettyType
            switch (data.type.toLowerCase()) {
              case 'grand_total':
                prettyType = 'GRAND TOTAL'
                break

              default:
                prettyType = 'UNKNOWN EXPENSES'
            }
            accountHierarchy.push(prettyType)
          }
          if (data.grandparent) accountHierarchy.push(data.grandparent)
          if (data.parent) accountHierarchy.push(data.parent)
          if (data.code) accountHierarchy.push(data.code)

          return accountHierarchy
        },

        rowClassRules: {
          'ag-changed': (params) => (params.data && params.data.edited),
          'ag-aggregateRow-0': (params) => (params.node.aggData && params.node.level === 0),
          'ag-aggregateRow-1': (params) => (params.node.aggData && params.node.level === 1),
          'ag-aggregateRow-2': (params) => (params.node.aggData && params.node.level >= 2),
          'ag-aggregateRow-beginning': (params) =>
            !params.node.level && !params.node.aggData && params.node.data && params.node.data.account_name === 'Starting Total',
          'ag-aggregateRow-changed': (params) =>
            !params.node.level && !params.node.aggData && params.node.data && params.node.data.account_name === 'Changes This Session',
          'ag-aggregateRow-total': (params) => {
            return !params.node.level && !params.node.aggData && params.node.data && params.node.data.account_name === 'Current Total'
          }
        },

        onGridReady: (params) => {
          this.$data.scope.onGridReady(params)
        },
        onFirstDataRendered: (params) => {
          this.$data.scope.onFirstDataRendered(params)
        },
        onCellValueChanged: (params) => {
          this.$data.scope.onCellValueChanged(params)
        }
      }

      this.autoGroupColumnDef = {
        headerName: 'Trans #',
        name: 'code',
        maxWidth: 200,
        minWidth: 100,
        cellRendererParams: {
          suppressCount: true
        },
        onCellClicked: (params) => {
          if (!params) return
          if (params && !params.data) return
          if (!['PO', 'AP', 'PC', 'JE', 'PR'].includes(params.data.trans_type)) return
          let transactionType = 'aps'
          const transactionId = params.data.subledger_id ? params.data.subledger_id : params.data.trans_number
          const type = params.data.trans_type.toLowerCase()
          switch (type) {
            case 'PO':
            case 'po':
              transactionType = 'pos'
              break
            case 'AP':
            case 'ap':
              transactionType = 'aps'
              break
            case 'PC':
            case 'pc':
              transactionType = 'pettycash'
              break
            case 'JE':
            case 'je':
              transactionType = 'jes'
              break
            case 'PR':
            case 'pr':
              transactionType = 'prs'
              break
          }
          var link = document.createElement('a')
          link.setAttribute('href', `/${transactionType}/${transactionId}`)
          link.setAttribute('target', '_blank')
          document.body.appendChild(link)
          link.click()
        },
        cellClass: (params) => (params.data && ['PO', 'AP', 'PC', 'JE', 'PR'].includes(params.data.trans_type) ? 'cell-blue' : ''),
        valueFormatter: (params) => (params.data ? this.buildTransData(params.data) : params.value),
        sortable: false,
        suppressSizeToFit: true,
        comparator: (nodeA, nodeB) => {
          if (nodeA.key < nodeB.key) {
            return -1
          } else if (nodeA.key > nodeB.key) {
            return 1
          } else {
            return 0
          }
        }
      }

      this.columnDefs = [
        this.$data.scope.accountTypeColumn,
        this.$data.scope.accountCodeColumn,
        this.$data.scope.accountNameColumn,
        this.$data.scope.descriptionColumn,
        this.$data.scope.vendorColumn,
        this.$data.scope.seriesColumn,
        this.$data.scope.locationColumn,
        this.$data.scope.setColumn,
        this.$data.scope.freeField1Column,
        this.$data.scope.freeField2Column,
        this.$data.scope.freeField3Column,
        this.$data.scope.freeField4Column,
        this.$data.scope.insuranceColumn,
        this.$data.scope.taxCodeColumn,
        this.$data.scope.assetColumn,
        this.$data.scope.documentNumberColumn,
        this.$data.scope.dateColumn,
        this.$data.scope.poNumberColumn,
        this.$data.scope.paymentNumberColumn,
        this.$data.scope.currencyColumn,
        this.$data.scope.amountColumn
      ]
    },
    created () {
      this.resetFilters()
      this.getOptions()
      // Load data to be used for validation
      this.disableReportRun = true
      this.isReady = true
      this.getAppSettings()
      this.getCompanyData()
        .then(response => {
          this.allCodes = Object.assign({}, response.data)
        })
        .catch(error => {
          console.log(error)
        })
        .finally(() => {
          this.reportOptions.report_currency_id = this.allCodes.currencies.find((c) => c.is_base).id

          this.getVendorData()
            .then((response) => {
              this.allCodes.vendors = sortObjectByProperty(response.data, 'code')
            })
            .catch((error) => {
              console.log(error)
            })

          this.disableReportRun = false
          this.isReady = false
        })
    },
    beforeRouteLeave (to, from, next) {
      if ((this.jeLinesToBeUpdated.length || this.poLinesToBeUpdated.length) && !this.$store.getters.getRouteLeaveConfirmation) {
        return this.$store.dispatch('setDestinationRoute', to)
        .then(() => this.$store.dispatch('changeModalVisibility', { entityType: 'changeLossModal', visible: true }))
        .catch(e => console.log(e))
      } else {
        next()
      }
    },
    beforeDestroy () {
      this.resetComponent()
    }
  }
</script>

<style scoped>
  .gl-reporter .form-control:focus {
    box-shadow: none;
    border-color: #ced4da;
  }

  .gl-reporter .custom-select {
    background: #ffffff;
  }

  .gl-reporter .input-group .input-group-text {
    padding: 0 0.5rem 0 0;
    color: #ced4da;
    font-size: 0.875rem;
    background: #fff;
    border-left: none;
  }

  .gl-reporter .ag-theme-bootstrap .ag-root {
    border: none !important;
  }

  .gl-reporter .progress {
    height: 4px;
    opacity: 1;
    background-color: #e8e8e8;
  }

  .gl-reporter .progress-bar {
    transition: none !important;
    transition: opacity 1.5s ease;
    background-color: #a4a5bb !important;
  }

  .gl-reporter .loaded .progress-bar {
    opacity: 0;
  }

  .gl-reporter .navbar {
    height: 38px !important;
    padding: 0;
    background-color: #e8e8e8;
  }

  .nav-item > a {
    color: #444444;
    margin: 2px 0 0 5px;
    padding-left: 1em;
    padding-right: 1em;
    font-weight: 300;
    background-color: #e0e0e0;
    border-right: solid 1px #e8e8e8;
    border-left: solid 1px #e8e8e8;
  }

  .oi {
      opacity: 0.8;
      color: #444444;
      margin-right: 0.5em;
  }

  .nav-item {
      font-size: 1rem;
  }

  .nav-item > a.active {
      color: #007bff;
      font-weight: 600;
      background-color: #f5f7f7;
  }

  .nav-item.disabled {
      color: #aaa !important;
      background-color: #f0f0f0 !important;
  }
  .nav-item.disabled .oi {
      color: #aaa !important;
  }

  .nav-item.show >a {
    background-color: #ffffff;
    border-right: solid 1px #000000;
  }

  .gl-reporter .navbar .navbar-nav .nav-item > a.active {
    color: #007bff !important;
    font-weight: 700;
    background-color: #f5f7f7;
  }

  .print-options {
    background-color: aliceblue;
    z-index: 1050;
    border: 1px solid #CCCCCC;
    right: 5px;
    top: 40px;
    position: absolute;
    color: #444444;
    font-weight: normal;
    width: 150px;
  }

  .print-options div {
    margin: 10px;
    cursor: pointer;
  }

  .print-options div:hover, .print-options div:active {
    font-weight: bold;
  }

  .dropdown-menu {
    top: 37px;
    margin: 0;
    border: none;
    border-bottom: solid 1px #000000;
    border-right: solid 1px #000000;
    border-left: solid 1px #000000;
    border-radius: 0;
    background-color: #ffffff;
  }

  .dropdown-menu .dropdown-item.dropdown-item:hover {
      background-color: #dfdfdf;
  }

  .dropdown-menu .dropdown-divider {
    border-top: 1px solid #d8d8d8;
  }

  .nav-info {
    background-color: #5bc0de !important;
    color: #fff !important;
    font-weight: 700 !important;
    border: 1px solid #bfcbd9 !important;
    border-radius: 4px;
  }

  .nav-blue {
      background-color: #007bff !important;
      color: #fff !important;
      font-weight: 600 !important;
      border: 1px solid #bfcbd9 !important;
      border-radius: 4px;
  }

  .nav-green {
      background-color: rgb(65, 152, 72) !important;
      color: #fff !important;
      font-weight: 700 !important;
      border: 1px solid #bfcbd9 !important;
      border-radius: 4px;
  }

  .nav-red {
      background-color: #ff0000c0 !important;
      color: #fff !important;
      font-weight: 700 !important;
      border: 1px solid #bfcbd9 !important;
      border-radius: 4px;
  }

  .white {
      color: #fff !important;
  }

  .black {
      color: #000 !important;
  }

  .bold {
      font-weight: 700 !important;
  }

  .clear {
      clear: both;
  }
  .small { font-size: 8px; }
  .right { float: right; text-align: right; padding: 0px 5px; }
  .left { float: left; text-align: left; padding: 0px 5px; }
  .selectRow { padding: 0px 20px; }
  .section { padding: 40px 20px 0px;  }
  .headerRow { padding: 15px 20px 0px; }
  .m-top-20 { margin-top: 20px; }
  .m-right-20 { margin-right: 20px; }
</style>

<style>
  .gl-reporter .el-select { display: block; }
  .el-transfer-panel { width:36%; max-width: 350px }
  .el-transfer-panel__body { min-height: 400px }
  .el-transfer-panel__list { min-height: 360px }
  .gl-reporter .ag-theme-balham .ag-row.ag-aggregateRow-0 {
    background-color: #67cbf8;
  }

  .gl-reporter .ag-theme-balham .ag-row.ag-aggregateRow-1 {
    background-color: #b3e5fc;
  }

  .gl-reporter .ag-theme-balham .ag-row.ag-aggregateRow-2 {
    background-color: #e1f5fe;
  }
  .gl-reporter .ag-theme-balham .ag-ltr .ag-cell.cell-blue {
    color: #007bff !important;
    cursor: pointer !important;
  }
  .gl-reporter .navbar-expand .navbar-nav .nav-link {
    padding-right: 1rem;
    padding-left: 1rem;
  }
  .gl-reporter .navbar-light .navbar-nav .nav-link.active.print {
    color: white !important;
  }
  .gl-reporter .navbar .navbar-nav .b-nav-dropdown .dropdown-menu {
    border: none !important;
    border-bottom: 1px solid #000 !important;
    border-right: 1px solid #000 !important;
    border-left: 1px solid #000 !important;
    border-radius: 0 !important;
    background-color: #fff !important;
    min-width: 12.3rem !important;
  }
  .gl-reporter .navbar .navbar-nav .b-nav-dropdown .dropdown-menu .custom-control-label {
    min-height: 1.5rem;
  }
  .gl-reporter .bicon {
    position: relative;
    color: #444;
    width: 14px;
    height: 14px;
    right: 4px;
    bottom: 2px;
  }
  .gl-reporter .bicon.tbl {
    right: 6px;
    bottom: 4px;
    width: 12px;
    height: 12px;
  }
  .gl-reporter .bicon.save {
    right: 6px;
    width: 12px;
    height: 12px;
    bottom: 3px;
  }
</style>


// WEBPACK FOOTER //
// src/components/Reports/GlReportCustom.vue